import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Col, Radio, RadioChangeEvent, Row } from 'antd';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import moment from 'moment';
import SessionContext from '@providers/Session/SessionContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Actives from './Actives';
import Usage from './Usage';
import StatusLines from './StatusLines';
import TradeIn from './TradeIn';
import OnlineOrOffline from './OnlineOrOffline';
import TotalLines from './TotalLines';
import IscaStatus from './IscaStatus';
import DevicesByDepartment from './DevicesByDepartment';
import * as S from './styles';

const Dashboard = () => {
  const { t } = useTranslation('inventory');
  const { keycloak } = useContext(KeycloakContext);
  const { isRoot, selectedApp, isMot, isMotBusiness, hasMotLorawan, isMotPay } =
    useContext(SessionContext);
  const navigate = useNavigate();

  const customerTypeLogged = keycloak.idTokenParsed?.customerType;
  const isEndCustomer = customerTypeLogged === 'CUSTOMER';

  const [showContracted, setShowContracted] = useState<'true' | 'false'>(
    'false',
  );

  useEffect(() => {
    if (isEndCustomer) {
      setShowContracted('true');
    }
  }, [isEndCustomer]);

  const showIscaData =
    isMot &&
    !isMotBusiness &&
    !isRoot &&
    (!showContracted || showContracted === 'false');

  const showDevicesByDepartments = isMotBusiness && showContracted === 'false';

  const showUsage = !isRoot && (!isMot || showContracted === 'true');

  const handleNavigate = useCallback(
    (search: string) => {
      const page = `/${selectedApp}/${
        hasMotLorawan ? 'mobile-network/' : ''
      }assets/inventory`;
      navigate(`${page}?${search}`, { replace: false });
    },
    [selectedApp, hasMotLorawan, navigate],
  );

  const handleLastConnection = useCallback(
    (days: number) => {
      const today = moment().toISOString();
      const from = moment().subtract(days, 'days').toISOString();
      handleNavigate(`lastConnectionFrom=${from}&lastConnectionTo=${today}`);
    },
    [handleNavigate],
  );

  const filters = useMemo(() => {
    const allFilters = {
      stock: undefined,
      showContracted: !isMot ? undefined : showContracted,
    };

    if (isMotPay && !isEndCustomer && !isRoot) {
      allFilters.showContracted = 'true';
    }

    return allFilters;
  }, [isEndCustomer, isMot, isMotPay, isRoot, showContracted]);

  return (
    <S.DashContainer>
      {isMot && !isRoot && !isEndCustomer && (
        <Radio.Group
          value={showContracted}
          style={{ paddingBottom: 16 }}
          onChange={(e: RadioChangeEvent) => setShowContracted(e.target.value)}
          buttonStyle="solid"
        >
          {['allocated', 'pay'].map(item => (
            <Radio.Button
              key={item}
              value={item === 'allocated' ? 'false' : 'true'}
            >
              {t(`visions-steps.${item}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
      )}

      <Row gutter={[16, 16]}>
        <Col xl={8} lg={12} xs={24}>
          <Actives
            onClick={() => handleNavigate('status=ACTIVE')}
            filters={filters}
          />
        </Col>
        <Col xl={8} lg={12} xs={24}>
          <OnlineOrOffline
            isOnline={false}
            onClick={() => handleNavigate('online=false')}
            filters={filters}
          />
        </Col>
        <Col xl={8} lg={12} xs={24}>
          <OnlineOrOffline
            isOnline
            onClick={() => handleNavigate('online=true')}
            filters={filters}
          />
        </Col>
        {!isMotBusiness && (!showContracted || showContracted === 'false') && (
          <Col xl={8} lg={12} xs={24}>
            <TradeIn
              onClick={() => {
                isMot
                  ? handleNavigate('status=TRADE_IN')
                  : handleNavigate('status=READY_TO_USE');
              }}
              filters={filters}
            />
          </Col>
        )}
        {showIscaData && (
          <Col xl={8} lg={12} xs={24}>
            <IscaStatus filters={{ showContracted }} />
          </Col>
        )}
        <Col xl={8} lg={12} xs={24}>
          <StatusLines
            allowMultiple
            onClick={days => handleLastConnection(days)}
            filters={filters}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {showDevicesByDepartments && (
          <Col span={24}>
            <DevicesByDepartment />
          </Col>
        )}

        <Col span={24}>
          <TotalLines filters={filters} />
        </Col>
      </Row>

      {showUsage && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Usage />
          </Col>
        </Row>
      )}
    </S.DashContainer>
  );
};

export default Dashboard;
